<template>
    <v-modal name="active-apps-selection" :width="600" height="auto" :pivotY="0.1" class="modal" scrollable
        @before-open="beforeOpen" @before-close="onBeforeClose">
        <h3 class="modal-title">{{ $t('ACTIVE_APPS_LIMIT_REACHED') }}</h3>

        <div class="active-apps-list">
            <div v-for="app in apps" :key="app.installation_history_uuid" class="app-row" :class="{
                'selected': selectedUuid === app.installation_history_uuid,
                'marked-for-removal': appsToRemove.includes(app.installation_history_uuid)
            }" @click="selectApp(app)">
                <div class="app-info">
                    <img :src="app.icon_uri" :alt="app.name" class="app-icon">
                    <span class="app-name">{{ app.name }}</span>
                </div>
                <div class="status-container">
                    <span v-if="selectedUuid === app.installation_history_uuid" class="status-text status-replace">{{
                        $t('WILL_BE_REPLACED') }}</span>
                    <span v-else-if="appsToRemove.includes(app.installation_history_uuid)"
                        class="status-text status-forget">{{ $t('WILL_BE_FORGOTTEN') }}</span>
                </div>
                <div class="app-actions">
                    <button class="remove-button" :class="{
                        'active': appsToRemove.includes(app.installation_history_uuid),
                        'replace': selectedUuid === app.installation_history_uuid
                    }" @click.stop="toggleRemove(app)">
                        {{ getButtonText(app) }}
                    </button>
                </div>
            </div>
        </div>

        <div class="modal-footer">
            <button class="g-button" @click="cancel">{{ $t('CANCEL') }}</button>
            <button class="g-button g-button_primary" @click="proceed">{{ $t('CONTINUE') }}</button>
        </div>
    </v-modal>
</template>

<script>
import VModal from '@/components/VModal.vue';

export default {
    components: {
        VModal,
    },
    data() {
        return {
            apps: [],
            selectedUuid: null,
            appsToRemove: [],
            originalError: null,
            installOptions: null,
            doInstall: null,
            resolve: null,
            reject: null,
            handleCancel: null,
        };
    },
    methods: {
        beforeOpen(event) {
            this.apps = event.params.apps || [];
            this.selectedUuid = null;
            this.appsToRemove = [];
            this.originalError = event.params.originalError;
            this.installOptions = event.params.installOptions;
            this.doInstall = event.params.doInstall;
            this.resolve = event.params.resolve;
            this.reject = event.params.reject;
            this.handleCancel = event.params.handleCancel || (() => { });
        },
        selectApp(app) {
            if (this.appsToRemove.includes(app.installation_history_uuid)) {
                return;
            }

            if (this.selectedUuid === app.installation_history_uuid) {
                this.selectedUuid = null;
            } else {
                this.selectedUuid = app.installation_history_uuid;
            }
        },
        toggleRemove(app) {
            const index = this.appsToRemove.indexOf(app.installation_history_uuid);
            if (index === -1) {
                this.appsToRemove.push(app.installation_history_uuid);
            } else {
                this.appsToRemove.splice(index, 1);
            }

            if (this.selectedUuid === app.installation_history_uuid) {
                this.selectedUuid = null;
            }
        },
        getButtonText(app) {
            if (this.appsToRemove.includes(app.installation_history_uuid)) {
                return this.$t('TO_FORGET');
            }
            if (this.selectedUuid === app.installation_history_uuid) {
                return this.$t('TO_REPLACE');
            }
            return this.$t('NOT_USING');
        },
        cancel() {
            console.log('[DEBUG] Cancel button clicked');
            this.$modal.hide('active-apps-selection');
            console.log('[DEBUG] Modal hide requested');

            if (typeof this.handleCancel === 'function') {
                console.log('[DEBUG] Calling handleCancel function');
                this.handleCancel();
            } else {
                console.warn('[DEBUG] handleCancel is not a function:', this.handleCancel);
            }
        },
        async proceed() {
            console.log('[DEBUG] Proceed button clicked');

            try {
                // Check if the user has made a selection or marked apps for removal
                if (!this.selectedUuid && this.appsToRemove.length === 0) {
                    console.log('[DEBUG] User clicked continue without making a selection - keeping modal open');
                    // Show notification but keep the modal open
                    this.$notify({
                        type: 'info',
                        text: this.$t('PLEASE_SELECT_APP_OR_MARK'),
                        duration: 3000
                    });
                    // Do not hide the modal if no selection was made - keep it open
                    return;
                }

                // User has made a selection, now we can hide the modal
                this.$modal.hide('active-apps-selection');
                console.log('[DEBUG] Modal hide requested for proceed');

                // If any apps are marked for removal, call set_apps_removed
                if (this.appsToRemove.length > 0) {
                    console.log('[DEBUG] Removing apps:', this.appsToRemove);
                    await this.$api.call('set_apps_removed', {
                        installation_history_uuids: this.appsToRemove
                    });
                } else {
                    console.log('[DEBUG] No apps marked for removal');
                }

                // Find bundle_id of selected app, if any
                let bundleId = null;
                if (this.selectedUuid) {
                    const selectedApp = this.apps.find(app => app.installation_history_uuid === this.selectedUuid);
                    if (selectedApp) {
                        bundleId = selectedApp.bundle_id;
                        console.log('[DEBUG] Selected app bundle_id:', bundleId);
                    }
                } else {
                    console.log('[DEBUG] No app selected for replacement');
                }

                // Continuing with installation with the selected options
                console.log('[DEBUG] Continuing with installation, additionalOptions:', bundleId ? { bundle_id: bundleId } : {});

                // Retry installation with bundle_id if selected
                const additionalOptions = bundleId ? { bundle_id: bundleId } : {};
                const result = await this.doInstall(additionalOptions);
                console.log('[DEBUG] Installation result after proceed:', result);
                this.resolve(result);
            } catch (error) {
                console.error('[DEBUG] Error in proceed method:', error);
                if (typeof this.reject === 'function') {
                    this.reject(error);
                } else {
                    console.error('[DEBUG] reject is not a function:', this.reject);
                    // Simply re-throw the error since we can't reject
                    throw error;
                }
            }
        },
        onBeforeClose(event) {
            console.log('[DEBUG] Modal before-close triggered:', event);
            // If closed by clicking outside or ESC key, trigger the cancel handler
            if (event.type === 'outside-click' || event.cancel) {
                console.log('[DEBUG] Handling modal outside click or ESC');
                this.handleCancel();
            }
        },
    }
};
</script>

<style lang="scss" scoped>
.modal-title {
    margin-top: 0;
    margin-bottom: 15px;
    font-size: 16px;
    font-weight: normal;
    color: var(--text-primary);
}

.active-apps-list {
    max-height: 60vh;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
}

.app-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px;
    margin: 5px 0;
    border-radius: 4px;
    background-color: var(--secondary-bg);
    cursor: pointer;
    transition: all 0.2s;
    color: var(--text-primary);

    &.selected {
        background-color: var(--success-light-bg);
        border: 1px solid var(--success-border, #4caf50);
    }

    &.marked-for-removal {
        background-color: var(--error-light-bg);
        border: 1px solid var(--error-border, #f44336);
    }
}

.app-info {
    display: flex;
    align-items: center;
    min-width: 150px;
    flex: 1 1 auto;
    overflow: hidden;
    margin-right: 10px;
}

.status-container {
    flex: 0 1 auto;
    text-align: center;
    padding: 0 10px;
    display: flex;
    justify-content: flex-start;
    white-space: nowrap;
}

.app-icon {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    margin-right: 10px;
}

.app-name {
    font-weight: 500;
    color: var(--text-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 150px;
}

.status-text {
    font-size: 14px;
    font-style: italic;

    &.status-replace {
        color: var(--text-success, #2e7d32);
    }

    &.status-forget {
        color: var(--text-error, #c62828);
    }
}

.app-actions {
    display: flex;
    justify-content: flex-end;
    min-width: 100px;
    flex: 0 0 auto;
}

.remove-button {
    padding: 6px 12px;
    border-radius: 4px;
    font-size: 14px;
    cursor: pointer;
    border: 1px solid var(--tertiary-accent);
    background-color: var(--tertiary-bg);
    color: var(--text-secondary);
    transition: all 0.2s;
    min-width: 90px;
    text-align: center;

    &.active {
        background-color: var(--error-light-bg);
        color: var(--text-error, #d32f2f);
        border-color: var(--error-border, #f44336);
    }

    &.replace {
        background-color: var(--success-light-bg);
        color: var(--text-success, #2e7d32);
        border-color: var(--success-border, #4caf50);
    }

    &:hover {
        background-color: var(--hover-bg);
    }

    &.active:hover {
        background-color: var(--error-hover-bg, #ffcdd2);
    }

    &.replace:hover {
        background-color: var(--success-hover-bg, #c8e6c9);
    }
}

.modal-footer {
    display: flex;
    justify-content: flex-end;
    padding-top: 15px;
    margin-top: 15px;
    border-top: 1px solid var(--tertiary-accent);

    button:last-child {
        margin-left: 10px;
    }
}
</style>